import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Scale, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const VolumeBalancer = ({ filters, reducedMotion }) => {
  const [loading, setLoading] = useState(true);
  const [balanceData, setBalanceData] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchVolumeBalance();
  }, [filters]);

  const fetchVolumeBalance = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/insights/v2/volume-balance`);
      const data = await response.json();
      setBalanceData(data);
    } catch (error) {
      console.error('Failed to fetch volume balance:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBalanceIcon = (variance) => {
    if (Math.abs(variance) < 0.05) return <Minus className="w-4 h-4 text-emerald-400" />;
    return variance > 0 ? <TrendingUp className="w-4 h-4 text-ink-500" /> : <TrendingDown className="w-4 h-4 text-amber-400" />;
  };

  const getBalanceColor = (variance) => {
    if (Math.abs(variance) < 0.05) return 'text-emerald-400';
    if (Math.abs(variance) < 0.15) return 'text-ink-500';
    return 'text-amber-400';
  };

  return (
    <Card className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 border-slate-700/50 backdrop-blur-xl" data-testid="volume-balancer-card">
      <CardHeader>
        <CardTitle className="text-xl text-slate-100 flex items-center gap-2">
          <Scale className="w-5 h-5 text-indigo-400" />
          Call Volume Balancer
        </CardTitle>
        <CardDescription className="text-slate-400 mt-1">
          Real-time load distribution across campaigns and agents
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 bg-slate-800/30 animate-pulse rounded-lg" />
            ))}
          </div>
        ) : balanceData ? (
          <div className="space-y-6">
            {/* Overall Balance Score */}
            <div className="text-center p-6 bg-slate-800/30 rounded-xl border border-slate-700/50">
              <p className="text-sm text-slate-400 mb-2">Overall Balance Score</p>
              <div className="flex items-center justify-center gap-3">
                <p className="text-5xl font-bold text-emerald-400" data-testid="balance-score">
                  {(balanceData.overall_balance_score * 100).toFixed(0)}
                </p>
                <div className="text-left">
                  <p className="text-2xl font-bold text-slate-100">/100</p>
                  <p className="text-xs text-slate-500">Excellent</p>
                </div>
              </div>
            </div>

            {/* Campaign Distribution */}
            <div>
              <h4 className="text-sm font-medium text-slate-300 mb-3">Campaign Distribution</h4>
              <div className="space-y-3">
                {balanceData.campaigns?.map((campaign, index) => (
                  <div
                    key={campaign.id}
                    className="p-4 bg-slate-800/30 rounded-lg border border-slate-700/50"
                    style={{
                      animation: reducedMotion ? 'none' : `fadeIn 0.3s ease-out ${index * 0.05}s both`
                    }}
                    data-testid={`campaign-balance-${campaign.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {getBalanceIcon(campaign.variance)}
                        <span className="font-medium text-slate-100" data-testid="campaign-name">{campaign.name}</span>
                      </div>
                      <Badge
                        className={campaign.status === 'balanced' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' : 'bg-amber-500/20 text-amber-400 border-amber-500/30'}
                        data-testid="campaign-status"
                      >
                        {campaign.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-3">
                      <div>
                        <p className="text-xs text-slate-400">Current Volume</p>
                        <p className="text-lg font-semibold text-slate-100" data-testid="current-volume">{campaign.current_volume}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Target Volume</p>
                        <p className="text-lg font-semibold text-slate-100" data-testid="target-volume">{campaign.target_volume}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Variance</p>
                        <p className={`text-lg font-semibold ${getBalanceColor(campaign.variance)}`} data-testid="variance">
                          {campaign.variance > 0 ? '+' : ''}{(campaign.variance * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="mt-3 h-2 bg-slate-700/50 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-indigo-500 to-ink-500 rounded-full transition-all duration-500"
                        style={{ width: `${(campaign.current_volume / campaign.target_volume) * 100}%` }}
                        data-testid="progress-bar"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <p className="text-slate-400 text-center py-8">No volume data available</p>
        )}
      </CardContent>
    </Card>
  );
};

export default VolumeBalancer;